import { tx as translateInterfaceText } from '@/utils/i18n';
import { api } from './api';
import {
  ShareLink,
  ShareDetails,
  VideoWithShareStatus,
  ListVideosForShareParams,
  ListVideosForShareResult,
  PublicShareLandingData,
  PublicShareLandingAuthData,
  SharedVideoData,
  SharedVideoWhosGet
} from '@/types/share';
import {
  getStoredUser,
  getChartDataByDay,
  getTopCountries,
  getTopStates,
  backendToShareLink,
  whosGetToEvents
} from '@/helpers/share/shareHelpers';

// ========== LOW-LEVEL API TYPES ==========

interface ShareResponse {
  success: boolean;
  message: string;
  data: SharedVideoData;
}

interface ShareListResponse {
  success: boolean;
  message: string;
  data: SharedVideoData[];
}

interface ShareStatusResponse {
  success: boolean;
  message: string;
}

interface ShareGettingResponse {
  success: boolean;
  message: string;
}

// ========== LOW-LEVEL API SERVICE ==========

const shareVideoApi = {
  /**
   * POST /share-video/share
   * Cria um link de compartilhamento para um vídeo
   */
  share: async (idUser: string, idVideo: string): Promise<ShareResponse> => {
    const response = await api.post('/share-video/share', {
      id_user: idUser,
      id_video: idVideo,
    });
    if (!response.success) {
      throw new Error(response.message || translateInterfaceText("Erro ao criar link de compartilhamento"));
    }
    return response;
  },

  /**
   * POST /share-video/change-status
   * Ativa/desativa o compartilhamento de um vídeo
   */
  changeStatus: async (idUser: string, hash: string, status: boolean): Promise<ShareStatusResponse> => {
    const response = await api.post('/share-video/change-status', {
      id_user: idUser,
      hash,
      status,
    });
    if (!response.success) {
      throw new Error(response.message || translateInterfaceText("Erro ao alterar status do link"));
    }
    return response;
  },

  /**
   * GET /share-video/find-by-hash/:hash
   * Busca dados de compartilhamento pelo hash (usado na landing page pública)
   */
  findByHash: async (hash: string): Promise<ShareResponse> => {
    const response = await api.get(`/share-video/find-by-hash/${hash}`);
    if (!response.success) {
      throw new Error(response.message || translateInterfaceText("Link não encontrado"));
    }
    return response;
  },

  /**
   * GET /share-video/find-by-video-user-id/:id_user/:id_video
   * Busca dados de compartilhamento de um vídeo específico do usuário
   */
  findByUserIdVideoId: async (idUser: string, idVideo: string): Promise<ShareResponse> => {
    const response = await api.get(`/share-video/find-by-video-user-id/${idUser}/${idVideo}`);
    if (!response.success) {
      throw new Error(response.message || translateInterfaceText("Compartilhamento não encontrado"));
    }
    return response;
  },

  /**
   * GET /share-video/find-by-user-id/:id_user
   * Lista todos os compartilhamentos do usuário
   */
  findByUserId: async (idUser: string): Promise<ShareListResponse> => {
    const response = await api.get(`/share-video/find-by-user-id/${idUser}`);
    if (!response.success) {
      throw new Error(response.message || translateInterfaceText("Erro ao buscar compartilhamentos"));
    }
    return response;
  },

  /**
   * POST /share-video/getting
   * Usuário recebe/copia o vídeo compartilhado
   */
  getSharedVideo: async (idUser: string, hash: string, userIp: string): Promise<ShareGettingResponse> => {
    const response = await api.post('/share-video/getting', {
      id_user: idUser,
      hash,
      userIp,
    });
    if (!response.success) {
      throw new Error(response.message || translateInterfaceText("Erro ao processar compartilhamento"));
    }
    return response;
  },

  /**
   * DELETE /share-video/remove/:id_user/:id_video
   * Remove/exclui o compartilhamento de um vídeo
   */
  remove: async (idUser: string, idVideo: string): Promise<ShareStatusResponse> => {
    const response = await api.delete(`/share-video/remove/${idUser}/${idVideo}`);
    if (!response.success) {
      throw new Error(response.message || translateInterfaceText("Erro ao excluir compartilhamento"));
    }
    return response;
  },
};

// ========== HIGH-LEVEL API FUNCTIONS ==========

export async function listVideosForShare(params: ListVideosForShareParams = {}): Promise<ListVideosForShareResult> {
  const user = getStoredUser();
  if (!user) throw new Error(translateInterfaceText("Usuário não autenticado"));

  const shareRes = await shareVideoApi.findByUserId(user.id_user);
  const shareRecords = Array.isArray(shareRes.data) ? shareRes.data : [];

  let enriched: VideoWithShareStatus[] = shareRecords.map((record: any) => {
    const shareLink = backendToShareLink(record);

    return {
      id: record.id_video,
      ownerUserId: record.id_user,
      title: record.nomeVideo || `Vídeo ${record.id_video}`,
      thumbUrl: record.frame || "",
      duration: 0,
      status: "ready",
      createdAt: record.createdAt,
      shareLink,
      copiesCount: shareLink.copiesCount,
      lastCopyAt: record.updatedAt || record.createdAt,
    };
  });

  if (params.q) {
    const query = params.q.toLowerCase();
    enriched = enriched.filter((v) => v.title.toLowerCase().includes(query));
  }

  if (params.filter && params.filter !== "all") {
    enriched = enriched.filter((v) => {
      if (params.filter === "active") return v.shareLink?.status === "active";
      if (params.filter === "revoked") return v.shareLink?.status === "revoked";
      return true;
    });
  }

  return { videos: enriched };
}

export async function getShareDetails(videoId: string): Promise<ShareDetails> {
  const user = getStoredUser();
  if (!user) throw new Error(translateInterfaceText("Usuário não autenticado"));

  const res = await shareVideoApi.findByUserIdVideoId(user.id_user, videoId);
  const record: any = res.data;
  if (!record) throw new Error(translateInterfaceText("Compartilhamento não encontrado"));

  const shareLink = backendToShareLink(record);
  const events = whosGetToEvents(record.whos_get, record);

  const videoInfo = {
    id: record.id_video,
    title: record.nomeVideo || `Vídeo ${record.id_video}`,
    thumbUrl: record.frame || "",
    duration: 0,
  };

  const kpis = {
    totalCopies: events.length,
    last24h: 0,
    successRate: events.length > 0 ? 100 : 0,
    countriesReached: new Set(events.map((e) => e.geo.country).filter(Boolean)).size,
  };

  return {
    video: videoInfo,
    shareLink,
    kpis,
    chartByDay: getChartDataByDay(events, 14),
    topCountries: getTopCountries(events),
    topStates: getTopStates(events),
    events,
  };
}

export async function createShareLink(videoId: string): Promise<{ shareLink: ShareLink }> {
  const user = getStoredUser();
  if (!user) throw new Error(translateInterfaceText("Usuário não autenticado"));

  const res = await shareVideoApi.share(user.id_user, videoId);

  return { shareLink: backendToShareLink(res.data) };
}

export async function revokeShareLink(videoId: string): Promise<{ ok: boolean }> {
  const user = getStoredUser();
  if (!user) throw new Error(translateInterfaceText("Usuário não autenticado"));

  const detailRes = await shareVideoApi.findByUserIdVideoId(user.id_user, videoId);
  const record = detailRes.data;
  if (!record) throw new Error(translateInterfaceText("Nenhum link encontrado para este vídeo"));

  await shareVideoApi.changeStatus(user.id_user, record.hash, false);

  return { ok: true };
}

export async function reactivateShareLink(videoId: string): Promise<{ ok: boolean }> {
  const user = getStoredUser();
  if (!user) throw new Error(translateInterfaceText("Usuário não autenticado"));

  const detailRes = await shareVideoApi.findByUserIdVideoId(user.id_user, videoId);
  const record = detailRes.data;
  if (!record) throw new Error(translateInterfaceText("Nenhum link encontrado para este vídeo"));

  await shareVideoApi.changeStatus(user.id_user, record.hash, true);

  return { ok: true };
}

export async function deleteShareLink(videoId: string): Promise<{ ok: boolean }> {
  const user = getStoredUser();
  if (!user) throw new Error(translateInterfaceText("Usuário não autenticado"));

  await shareVideoApi.remove(user.id_user, videoId);

  return { ok: true };
}

export async function regenerateShareLink(videoId: string) {
  const user = getStoredUser();
  if (!user) throw new Error(translateInterfaceText("Usuário não autenticado"));

  try {
    await deleteShareLink(videoId);
  } catch {
    // If delete fails, continue to create new one
  }

  return createShareLink(videoId);
}

// ========== PUBLIC ENDPOINTS (Share Landing) ==========

export async function publicGetShareLanding(shareCode: string): Promise<PublicShareLandingData> {
  try {
    const res = await shareVideoApi.findByHash(shareCode);

    if (!res.data) {
      return { status: "not_found" };
    }

    const record: any = res.data;

    if (!record.active) {
      return { status: "revoked" };
    }

    return {
      status: "active",
      ownerId: record.id_user,
      ownerName: record.ownerName || "",
      videoPreview: {
        title: record.videoTitle || record.id_video,
        thumbUrl: "",
        duration: 0,
      },
      settings: {},
    };
  } catch {
    return { status: "not_found" };
  }
}

export async function publicGetShareLandingAuthenticated(
  shareCode: string,
  userId: string
): Promise<PublicShareLandingAuthData> {
  try {
    const res = await shareVideoApi.findByHash(shareCode);

    if (!res.data) {
      return { status: "not_found" };
    }

    const record: any = res.data;

    if (!record.active) {
      return { status: "revoked" };
    }

    const isOwner = record.id_user === userId;

    return {
      status: "active",
      isOwner,
      ownerId: record.id_user,
      ownerName: record.ownerName || "",
      videoPreview: {
        title: record.videoTitle || record.id_video,
        thumbUrl: "",
        duration: 0,
      },
      settings: {},
    };
  } catch {
    return { status: "not_found" };
  }
}

export async function publicConfirmCopyBySession(
  shareCode: string,
  userId: string
): Promise<{ status: string; error?: string; code?: string }> {
  try {
    let userIp = "";
    try {
      const ipRes = await fetch("https://api.ipify.org?format=json");
      const ipData = await ipRes.json();
      userIp = ipData.ip || "";
    } catch {
      // IP fetch failed, continue without it
    }

    await shareVideoApi.getSharedVideo(userId, shareCode, userIp);

    return { status: "success" };
  } catch (err: any) {
    return {
      status: "failed",
      error:
        err?.response?.data?.message ||
        err?.message ||
        "Erro ao processar vídeo. Tente novamente.",
      code: err?.response?.data?.code,
    };
  }
}

// ========== ANALYTICS / TRACKING ==========

export function trackShareEvent(event: string, data: any) {
  console.log(`[Analytics] ${event}`, data);
}

// Export for backward compatibility (if needed)
export const shareVideoService = shareVideoApi;

// Stub for advanced settings update (not yet implemented in backend)
export async function updateShareLinkSettings(videoId: string, settings: Record<string, any>): Promise<{ ok: boolean }> {
  console.log('[shareVideoService] updateShareLinkSettings stub', videoId, settings);
  return { ok: true };
}

// Export types for backward compatibility
export type { SharedVideoData, SharedVideoWhosGet, ShareFilter, VideoWithShareStatus } from '@/types/share';
